<template>
  <div class="page-root dark" tabindex="0">
    <div class="layout">

      <!-- ── Sidebar ── -->
      <aside class="sidebar">
        <div class="sidebar-inner">
          <v-img
              src="@/assets/ali-bayani.jpg"
              class="profile-pic rounded-xl"
              max-width="150px"
          ></v-img>

          <nav class="socials" aria-label="Social links">
            <a :href="links.github" target="_blank" rel="noopener" class="social-link">
              <img src="https://cdn.simpleicons.org/github" class="social-icon" alt="GitHub" width="16" height="16" />
              <span>GitHub</span>
            </a>
            <a :href="links.scholar" target="_blank" rel="noopener" class="social-link">
              <img src="https://cdn.simpleicons.org/googlescholar" class="social-icon" alt="Google Scholar" width="16" height="16" />
              <span>Google Scholar</span>
            </a>
            <a :href="links.orcid" target="_blank" rel="noopener" class="social-link">
              <img src="https://cdn.simpleicons.org/orcid" class="social-icon" alt="ORCID" width="16" height="16" />
              <span>ORCID</span>
            </a>
            <a :href="links.linkedin" target="_blank" rel="noopener" class="social-link">
              <img src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@latest/icons/linkedin.svg" class="social-icon" alt="LinkedIn" width="16" height="16" />
              <span>LinkedIn</span>
            </a>
            <a :href="links.researchgate" target="_blank" rel="noopener" class="social-link">
              <img src="https://cdn.simpleicons.org/researchgate" class="social-icon" alt="ResearchGate" width="16" height="16" />
              <span>ResearchGate</span>
            </a>
            <a :href="links.kaggle" target="_blank" rel="noopener" class="social-link">
              <img src="https://cdn.simpleicons.org/kaggle" class="social-icon" alt="Kaggle" width="16" height="16" />
              <span>Kaggle</span>
            </a>
            <a :href="'mailto:' + contact.email" class="social-link">
              <!-- Inline SVG — no file needed -->
              <svg class="social-icon email-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              <span>Email</span>
            </a>
          </nav>
        </div>
      </aside>

      <!-- ── Main content ── -->
      <main class="content">

        <!-- Intro -->
        <section class="card intro reveal">
          <div class="intro-grid">
            <div>
              <h2>Hello!</h2>
              <p class="lead">
                I am an AI/ML researcher and MSc student in Computer Science at the University of Debrecen, with a BSc in Computer Engineering. My research focuses on continual learning and AI alignment, and my applied work spans interdisciplinary projects, especially in healthcare.
                <br><br>
                I am always open to collaboration!
              </p>
            </div>
          </div>
        </section>

        <!-- Publications -->
        <section id="publications" class="card reveal">
          <div class="section-heading">
            <h3 class="section-title" style="color: #913831">Research Publications</h3>
            <hr><br>
          </div>

          <ol class="publications">
            <li v-for="(p, idx) in visiblePublications" :key="idx" class="publication">
              <div class="pub-meta">
                <div class="pub-title">{{ p.title }}</div>
                <div class="pub-authors">{{ p.author }}</div>
                <div class="pub-venue">
                  <span v-if="p.journal">{{ p.journal }}</span>
                  <span v-else-if="p.booktitle">{{ p.booktitle }}</span>
                  <span v-if="p.date"> - {{ p.date }}</span>
                  <span v-else-if="p.year"> - {{ p.year }}</span>
                </div>
              </div>
              <div class="pub-links">
                <a v-if="p.url" :href="p.url" target="_blank" rel="noopener">Link</a>
                <a v-if="p.doi" :href="formatDOI(p.doi)" target="_blank" rel="noopener">DOI</a>
                <a v-if="p.pdf" :href="p.pdf" target="_blank" rel="noopener">PDF</a>
              </div>
            </li>
          </ol>

          <p v-if="publications.length === 0" class="muted">
            No publications loaded. (You can edit the `publications` array in the component.)
          </p>
        </section>

        <!-- Projects -->
        <section id="projects" class="card reveal">
          <h3 class="section-title" style="color: #913831">Projects</h3>
          <ul class="projects"><hr>
            <li v-for="(pr, i) in projects" :key="i">
              <strong>{{ pr.title }}</strong> <span>{{ pr.desc }}</span>
              <a v-if="pr.link" :href="pr.link" target="_blank" rel="noopener" class="proj-link" style="color: #D4AF37;">[repo]</a>
            </li>
          </ul>
        </section>

        <!-- Awards / Teaching / Peer review (single column) -->
        <section class="cards-stacked">
          <div id="honors" class="card reveal">
            <h4 style="color: #913831">Awards & Honors</h4>
            <ul>
              <li v-for="(a, i) in awards" :key="i">
                <strong>{{ a.title }}:</strong> {{ a.description }}
                <template v-if="a.link"> [<a :href="a.link" target="_blank" rel="noopener">{{ a.linkLabel }}</a>]</template>
                <em v-if="a.date"> - {{ a.date }}</em>
              </li>
            </ul>
          </div>

          <div id="teaching" class="card reveal">
            <h4 style="color: #913831">Teaching</h4>
            <ul>
              <li v-for="(t, i) in teaching" :key="i">{{ t }}</li>
            </ul>
          </div>

          <div id="peerreview" class="card reveal">
            <h4 style="color: #913831">Peer Review</h4>
            <ul>
              <li v-for="(r, i) in peerReview" :key="i">{{ r }}</li>
            </ul>
          </div>
        </section>

        <!-- Contact -->
        <section id="contact" class="card reveal contact-card">
          <h3 class="section-title" style="color: #913831">Contact</h3>
          <p>Email: <a :href="'mailto:' + contact.email">{{ contact.email }}</a></p>
          <p>Affiliation: Faculty of Informatics, University of Debrecen, Hungary</p>
        </section>

        <footer class="footer">
          <small>Last updated: {{ lastUpdated }} • © {{ currentYear }} Ali Bayani</small>
        </footer>
      </main>

    </div>
  </div>
</template>

<script>
export default {
  name: "ResumePage",
  data() {
    return {
      contact: { email: "aliwbayani@gmail.com" },
      links: {
        github:      "https://github.com/aliebayani",
        scholar:     "https://scholar.google.com/citations?user=bACdbPYAAAAJ&hl=en",
        orcid:       "https://orcid.org/0009-0003-6770-0546",
        linkedin:    "https://www.linkedin.com/in/ali-bayani-a13505209",
        researchgate:"https://www.researchgate.net/profile/Ali-Bayani-2",
        kaggle:      "https://www.kaggle.com/alibyni",
        scopus:      "https://www.scopus.com/authid/detail.uri?authorId=59216051700"
      },

      publications: [
        {
          title:   "AI-Based Ligand-Binding Site Prediction in Proteins: Methods, Benchmarks, Challenges, and Future Directions",
          author:  "Abbaskhani, S.; Farhadi, A.; Shafaei, F.; Bayani, A.",
          journal: "SSRN preprint",
          date:    "Aug 28, 2026",
          doi:     "10.2139/ssrn.7372540",
          url:     "https://doi.org/10.2139/ssrn.7372540"
        },
        {
          title:   "DPANet: Dual-Encoder Protein Attention Network for Secondary Structure Prediction",
          author:  "Abbaskhani, S.; Kargar, M.; Bayani, A.; Assadzadeh, A.",
          journal: "IEEE Access",
          year:    "2026",
          doi:     "10.1109/ACCESS.2026.3704132",
          url:     "https://doi.org/10.1109/ACCESS.2026.3704132"
        },
        {
          title:   "HAT‐ECG: Hybrid autoencoder‐transformer architecture for ECG arrhythmia classification",
          author:  "Movassaghpour, S.; Kargar, M.; Bayani, A.",
          journal: "Physiological Reports",
          year:    "2026",
          doi:     "10.14814/phy2.70990",
          url:     "https://doi.org/10.14814/phy2.70990"
        },
        {
          title:   "The Role of Artificial Intelligence and Machine Learning in Human Disease Diagnosis: A Comprehensive Review",
          author:  "Imani, F.; Bayani, A.; Kargar, M.; Assadzadeh, A.",
          journal: "Iran Journal of Computer Science",
          year:    "2025",
          doi:     "10.1007/s42044-025-00321-0",
          url:     "https://doi.org/10.1007/s42044-025-00321-0"
        },
        {
          title:   "Adaptive Portfolio Optimization with Multi-Agent Deep Reinforcement Learning and Short-Term Performance Analysis",
          author:  "Movassaghpour, S.; Kargar, M.; Bayani, A.; Assadzadeh, A.; Khakzadi, A.",
          journal: "International Journal of ICT Research (IEEE Iran Section)",
          year:    "2025",
          url:     "https://search.ebscohost.com/login.aspx?direct=true&profile=ehost&scope=site&authtype=crawler&jrnl=22516107&AN=188585732&h=7ImRCRt4RPApZFvuZyp42NvCTLusO9D4Wv1Yeyx%2FX9066ZJT9mZzVrZTs0IOuSI0WyHHqJxjPbpAbMbZlbMUrw%3D%3D&crl=c"
        },
        {
          title:   "HNMD-CNN: A Hierarchical Narrowing Multi-Deep Convolutional Neural Network for Precision Glioma Classification in 3D MRI Images",
          author:  "Hoseini, N.S.; Kargar, M.; Bayani, A.; Ardabili, S.",
          journal: "Computational and Systems Oncology",
          year:    "2025",
          doi:     "10.1002/cso2.70002",
          url:     "https://doi.org/10.1002/cso2.70002"
        },
        {
          title:   "Impact of Data Selection Strategies on EEG-Based Epilepsy Detection Using Stochastic Data Reduction and the EEG Continuity Principle",
          author:  "Hoseini, N.S.; Kargar, M.; Izadkhah, H.; Ahmadi, H.; Bayani, A.; Shokati A.",
          journal: "Preprint",
          year:    "2025",
          doi:     "10.22541/au.176419314.42726046/v1",
          url:     "https://doi.org/10.22541/au.176419314.42726046/v1"
        },
        {
          title:     "RBMD: RoBERTa-Based Module Detection in Multi-Programming Language Software Systems",
          author:    "Kargar, M.; Movassaghpour, S.; Bayani, A.",
          booktitle: "Proc. of 11th Int. Conf. on Web Research (ICWR)",
          year:      "2025",
          doi:       "10.1109/ICWR65219.2025.11006198",
          url:       "https://doi.org/10.1109/ICWR65219.2025.11006198"
        },
        {
          title:     "RDLHeart: Statistical Gene Selection with Deep Learning and SMOTE for Heart Failure Classification from Bulk RNA Sequencing",
          author:    "Assadzadeh, A.; Kargar, M.; Imani, F.; Abbaskhani, S.; Movassaghpour, S.; Bayani, A.",
          booktitle: "Development Engineering Conferences Center Articles Database",
          year:      "2025",
          url:       "https://pubs.bcnf.ir/index.php/Articles/article/view/1120"
        },
        {
          title:   "TransGAN-DX: A Hybrid Transformer-GAN Approach for Enhanced Cardiovascular Disease Diagnosis",
          author:  "Bayani, A.; Kargar, M.; Kargar, P.; Samadian, E.",
          journal: "American Hospital & Healthcare Management, Research Insights",
          year:    "2025",
          url:     "https://www.americanhhm.com/researchinsights/transgan-dx-a-hybrid-transformer-gan-approach-for-enhanced-cardiovascular-disease-diagnosis"
        },
        {
          title:   "AI-Driven Innovations in Cardiovascular Diagnostics and Remote Care",
          author:  "Bayani, A.; Kargar, M.; Samadian, E.",
          journal: "Asian Hospital & Healthcare Management, Issue 67, pp. 27-31",
          year:    "2025",
          url:     "https://www.asianhhm.com/archives-magazine/60",
          pdf:     "https://drive.google.com/file/d/1CDbYsHcJkw_hp7wLQM5SVBZKFsvppFkj/view?usp=drive_link"
        },
        {
          title:   "LDCNN: A new arrhythmia detection technique with ECG signals using a linear deep convolutional neural network",
          author:  "Bayani, A.; Kargar, M.",
          journal: "Physiological Reports",
          year:    "2024",
          doi:     "10.14814/phy2.16182",
          url:     "https://doi.org/10.14814/phy2.16182"
        },
        {
          title:     "Integrating CNN-LSTM Networks with Statistical Filtering Techniques for Intelligent IoT Intrusion Detection",
          author:    "Imani, F.; Kargar, M.; Assadzadeh, A.; Bayani, A.",
          booktitle: "Proc. of 8th Int. Conf. on Smart Cities, IoT and Applications (SCIoT)",
          year:      "2024",
          doi:       "10.1109/SCIoT62588.2024.10570107",
          url:       "https://doi.org/10.1109/SCIoT62588.2024.10570107"
        },
        {
          title:     "A Consulting System for Portfolio Assets Allocation by Selecting the Best Agent in the Short Term Based on Cumulative Returns with Deep Reinforcement Learning",
          author:    "Movassaghpour, S.; Kargar, M.; Bayani, A.; Assadzadeh, A.; Khakzadi, A.",
          booktitle: "Proc. of 11th Int. Symposium on Telecommunications (IST)",
          year:      "2024",
          doi:       "10.1109/IST64061.2024.10843551",
          url:       "https://doi.org/10.1109/IST64061.2024.10843551"
        }
      ],

      projects: [
        { title: "Unified AI-Aware Scheduler:",        desc: "Auto-Tuned Scheduler Prototype for Heterogeneous GPU Clusters. (2025)",           link: "https://github.com/aliebayani/Unified-AI-Aware-Scheduler" },
        { title: "HAT-ECG:",                           desc: "Hybrid Autoencoder-Transformer for ECG arrhythmia classification. (2025)",         link: "https://github.com/MasoudKargar/HAT-ECG" },
        { title: "RBMD:",                              desc: "RoBERTa-Based Module Detection in Multi-Language Software Systems. (2025)",         link: "https://github.com/MasoudKargar/RBMD" },
        { title: "TransGAN-DX:",                       desc: "Transformer-GAN pipeline for cardiovascular disease diagnosis. (2025)",            link: "https://github.com/aliebayani/TransGAN-DX" },
        { title: "EEG Epilepsy Detection:",            desc: "EEG epilepsy detection via data selection strategies. (2025)",                     link: "https://github.com/MasoudKargar/Data-Selection-EEG" },
        { title: "Portfolio Optimization with Deep RL:",desc: "Portfolio optimization using deep reinforcement learning. (2024–25)",             link: "https://github.com/MasoudKargar/APO-MADRL-STA" },
        { title: "HNMD-CNN:",                          desc: "Hierarchical deep CNN for glioma classification in 3D MRI. (2024–25)",             link: "https://github.com/MasoudKargar/HNMD-CNN" },
        { title: "IoT Intrusion Detection:",           desc: "CNN-LSTM with statistical filtering for IoT intrusion detection. (2024)",          link: "https://github.com/aliebayani/IoT-Devices-Intrusion-Detection" },
        { title: "LDCNN ECG Arrhythmia Detection:",   desc: "ECG-based arrhythmia detection using Linear Deep CNN. (2023)",                    link: "https://github.com/aliebayani/Linear-Deep-Convolutional-Neural-Network-LDCNN" },
        { title: "DPANet/GAN & Seq2Seq:",              desc: "Protein secondary structure prediction via LSTM, Seq2Seq & GAN. (2024)" },
        { title: "Real-Time Heart Arrhythmia Diagnosis:", desc: "Real-time heart arrhythmia detection via DRL & ECG. (2024)" },
        { title: "Disease Detection Using Deep & Machine Learning:", desc: "Machine learning and deep learning for disease detection. (2024–25)" },
        { title: "Solubility Prediction via GAN:",     desc: "Solubility prediction in ASD formulations using GAN. (2024)" },
        { title: "Quantum Control of XY Spin Chain:",  desc: "Quantum control of XY spin chain with deep reinforcement learning. (2024)" },
        { title: "Graph Theory for Software Architecture Modeling:", desc: "Graph-theory-based software architecture modeling. (2023)" },
        { title: "QA System with Transformer-Based RC:", desc: "Question Answering system using transformer-based reading comprehension. (2023)" },
        { title: "Social Media Text Clustering:",      desc: "Social media text clustering via Liquid Neural Networks. (2023)" },
        { title: "SocialMedia-Django:",                desc: "Lightweight quote-sharing platform (Internet Engineering Course Project)",          link: "https://github.com/aliebayani/SocialMedia-Django" },
        { title: "E-CRM Recommender System:",          desc: "Recommender system for e-CRM. (2022)" },
        { title: "Retinal Disease Classification:",    desc: "Retinal disease classification using deep learning. (2021)" }
      ],

      awards: [
        {
          title: "Stipendium Hungaricum Scholarship",
          description: "Full-degree government scholarship for an M.Sc. in Computer Science at the University of Debrecen, Hungary.",
          date: "Jun 2026"
        },
        {
          title: "Türkiye Scholarships",
          description: "Full-degree governmental graduate scholarship awarded by the Republic of Türkiye.",
          date: "Jun 2026"
        },
        {
          title: "Brock University Graduate Funding",
          description: "$31,548/year for an M.Sc. in Computer Science at Brock University, Canada.",
          date: "May 2026"
        },
        {
          title: "Prof. Kambiz Badie Award",
          description: "AI research presented at ICWR 2025.",
          date: "Apr 2025"
        },
        {
          title: "Featured Paper",
          description: "Selected as a featured paper at IST 2024.",
          date: "Dec 2024"
        },
        {
          title: "Wiley Research Hero Nominee",
          description: "Nominated for the Wiley Research Hero Prize for inclusive and innovative research.",
          date: "Nov 2024"
        },
        {
          title: "Second-Highest GPA",
          description: "Second-highest GPA among 308 Computer Engineering and IT students at Azad University of Tabriz.",
          date: "Oct 2023"
        }
      ],

      teaching: [
        "Teaching Assistant - Artificial Intelligence & Expert Systems - IAUT (Fall 2022)",
        "Teaching Assistant - Social Networks & Complex Networks - IAUT (Winter 2022)"
      ],

      peerReview: [
        "Engineering Applications of Artificial Intelligence",
        "IEEE Transactions on Artificial Intelligence",
        "Scientific Reports",
        "Computers & Electrical Engineering",
        "Expert Systems with Applications",
        "IEEE Access",
        "Information Sciences",
        "BMC Sports Science, Medicine and Rehabilitation",
        "Iran Journal of Computer Science",
        "Case Reports and Series in Medicine"
      ],

      showAll: true
    };
  },

  computed: {
    visiblePublications() {
      return this.showAll ? this.publications : this.publications.slice(0, 6);
    },
    lastUpdated() {
      return "September 2026";
    },
    currentYear() {
      return new Date().getFullYear();
    }
  },

  mounted() {
    const reveals = this.$el.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
        entries => {
          entries.forEach(e => {
            if (e.isIntersecting) {
              e.target.classList.add("in-view");
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.08 }
    );
    reveals.forEach(r => io.observe(r));
  },

  methods: {
    formatDOI(doi) {
      if (!doi) return "";
      if (doi.startsWith("http")) return doi;
      return "https://doi.org/" + doi.replace(/^doi:\s*/i, "");
    }
  }
};
</script>

<style scoped>

/* ── CSS Variables ── */
:root {
  --bg:      #0b1220;
  --card:    #071022;
  --muted:   #9aa4b2;
  --text:    #e6eef6;
  --accent:  #06b6d4;
  --glass:   rgba(255, 255, 255, 0.03);
  --shadow:  0 6px 20px rgba(2, 6, 23, 0.6);
  --radius:  10px;
  --gap:     20px;
}

/* ── Page root ── */
.page-root {
  background: linear-gradient(180deg, var(--bg), var(--bg));
  color: var(--text);
  min-height: 100vh;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
}

/* ── Two-column layout ── */
.layout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: var(--gap);
  max-width: 1200px;
  margin: 28px auto;
  padding: 0 18px;
  align-items: start;       /* keeps sidebar from stretching to content height */
}

/* ── Sidebar ── */
.sidebar {
  background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01));
  border-radius: var(--radius);
  padding: 16px 12px;
  box-shadow: var(--shadow);
  position: sticky;         /* sticks while scrolling on desktop */
  top: 80px;
}

.sidebar-inner {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

/* ── Social links ── */
.socials {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
  width: 100%;
}
.social-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text);
  text-decoration: none;
  font-size: 0.9rem;
  padding: 4px 0;
  transition: color 0.2s, opacity 0.2s;
}
.social-link:hover { color: #D4AF37; }
.social-link:hover .social-icon { opacity: 1; }

.social-icon {
  display: block;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  object-fit: contain;
  opacity: 0.55;              /* light / subtle by default */
  transition: opacity 0.2s;
}
/* Email SVG inherits text colour so it also turns gold on hover */
.email-svg {
  fill: currentColor;
  opacity: 0.55;
}
.social-link:hover .social-icon,
.social-link:hover .email-svg {
  opacity: 1;
}

/* ── Main content area ── */
.content { padding-bottom: 40px; }

/* ── Cards ── */
.card {
  background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01));
  border-radius: var(--radius);
  padding: 18px;
  margin-bottom: var(--gap);
  box-shadow: var(--shadow);
  /* reveal animation start state */
  opacity: 0;
  transform: translateY(12px) scale(0.995);
  transition: transform 0.28s cubic-bezier(0.2, 0.9, 0.3, 1), opacity 0.28s;
}
.card.in-view {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* ── Section typography ── */
.section-title { margin: 0 0 8px; font-size: 1.05rem; font-weight: 700; }
.lead          { margin: 8px 0 0; color: var(--muted); line-height: 1.6; }
.intro-grid    { display: flex; justify-content: space-between; gap: 12px; align-items: center; }

/* ── Publications ── */
.publications { list-style: none; padding-left: 0; margin: 0; display: flex; flex-direction: column; gap: 14px; }
.publication  { display: flex; justify-content: space-between; gap: 10px; align-items: flex-start; flex-wrap: wrap; }
.pub-meta     { flex: 1; min-width: 0; }
.pub-title    { font-weight: 600; color: var(--text); }
.pub-authors  { color: var(--muted); font-size: 0.9rem; margin-top: 2px; }
.pub-venue    { color: var(--muted); font-size: 0.85rem; margin-top: 2px; }
.pub-links    { display: flex; gap: 6px; flex-shrink: 0; flex-wrap: wrap; margin-top: 2px; }
.pub-links a  { color: var(--accent); text-decoration: none; font-size: 0.88rem; }
.pub-links a:hover { text-decoration: underline; }

/* ── Projects ── */
.projects      { list-style: none; padding-left: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.proj-link     { margin-left: 8px; font-size: 0.9rem; color: var(--accent); }

/* ── 3-column grid (Honors / Teaching / Peer Review) ── */
.grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.grid-3 .card { margin-bottom: 0; }

/* ── Contact ── */
.contact-card p  { margin: 6px 0; color: var(--muted); }
.contact-actions { margin-top: 10px; display: flex; flex-wrap: wrap; gap: 8px; }
.contact-actions a { color: var(--accent); text-decoration: none; }
.contact-actions a:hover { color: #D4AF37; }

/* ── Footer ── */
.footer { text-align: center; margin-top: 18px; color: var(--muted); }

/* ── Global link colours inside page ── */
a            { color: var(--accent); }
a:hover      { color: #D4AF37; }

/* ══════════════════════════════════════════
   RESPONSIVE — tablet (≤ 980px)
   Sidebar stacks above content, full width
   ══════════════════════════════════════════ */
@media (max-width: 980px) {
  .layout {
    grid-template-columns: 1fr;
    padding: 0 14px;
    gap: 14px;
    margin: 16px auto;
  }

  /* Sidebar sits at top (natural order = 1st in DOM) */
  .sidebar {
    position: static;          /* undo sticky */
    width: 100%;
    padding: 16px;
    box-sizing: border-box;
  }

  .sidebar-inner {
    flex-direction: row;       /* photo left, links right */
    align-items: flex-start;
    gap: 20px;
    flex-wrap: wrap;
  }

  .socials {
    margin-top: 0;
    flex-wrap: wrap;
    flex-direction: column;
    gap: 4px;
  }

  /* 3-col → 1-col */
  .grid-3 {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .publication {
    flex-direction: column;    /* stack links below metadata */
  }
}

/* ══════════════════════════════════════════
   RESPONSIVE — mobile (≤ 560px)
   Sidebar goes fully vertical / centered
   ══════════════════════════════════════════ */
@media (max-width: 560px) {
  .layout {
    padding: 0 10px;
  }

  .sidebar-inner {
    flex-direction: column;
    align-items: center;
  }

  .socials {
    align-items: flex-start;
    width: 100%;
  }

  .card {
    padding: 14px;
  }

  .pub-title {
    font-size: 0.95rem;
  }
}
</style>
